import { ArrowBack } from '@mui/icons-material';
import { Button, Stack } from '@mui/material';
import { QueryClient, queryOptions, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LoaderFunctionArgs, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import axiosInstance from '../../../../constants/axiosInstance';
import ErrorScreen from '../../../../core-components/theme/components/ErrorScreen';
import { StateArea } from '../../../../core-components/theme/components/Screen';
import FormSkeleton from '../FormSekeleton';
import { EscalationFormData, EscalationGroup } from './../../types';
import EscalationForm from './components/EscalationForm';

const fetchEscalationApi = async (escalationId: string) => {
    const response = await axiosInstance().post(`/escalation/getEscalationDetails`, { escalationId });
    return response.data?.data;
};

export const escalationDetailQuery = (escalationId: string) =>
    queryOptions({
      queryKey: ['escalation', escalationId],
      queryFn: async () => {
        const data = await fetchEscalationApi(escalationId);
        if (!data) {
          throw new Response('', {
            status: 404,
            statusText: 'Escalation Not Found',
          });
        }
        return data;
      },
      retry: 1,
      refetchOnWindowFocus: false,
      gcTime: 40000,
      staleTime: 100 * 10,
    });
  
  // Loader becomes simpler
  export const loader =
    (queryClient: QueryClient) =>
    async ({ params }: LoaderFunctionArgs) => {
      if (!params.escalationId) {
        throw new Error('No escalation ID provided');
      }
      
      const isEditMode = params.escalationId !== 'new';
      
      if (isEditMode) {
        await queryClient.ensureQueryData(escalationDetailQuery(params.escalationId));
      }
      
      return { 
        escalationId: params.escalationId,
        isEditMode 
      };
    };

const createDefaultEscalationGroup = (): EscalationGroup => ({
    id: uuidv4(),
    users: [],
    value: '',
    unit: 'Hour(s)',
});

const transformEscalationData = (data: any): EscalationFormData => {
    const modules: string[] = [];
    if (data.attendanceManager && data.attendanceManager.length > 0) {
        modules.push('Attendance');
    }
    if (data.escalations && data.escalations.length > 0) {
        modules.push('Task Escalation');
    }

    let escalationGroups =
        data.escalations?.map((esc: any) => ({
            id: esc.id || uuidv4(),
            users: esc.userIds || [],
            value: String(esc.value),
            unit: esc.unit,
        })) || [];

    if (modules.includes('Task Escalation') && escalationGroups.length === 0) {
        escalationGroups.push(createDefaultEscalationGroup());
    }

    return {
        userGroup: data.userGroup || '',
        plants: data.assets || [],
        modules: modules,
        attendanceManager: data.attendanceManager || [],
        rosterManager: data.rosterManager || [],
        operator: data.operator || [],
        escalationGroups: escalationGroups,
    };
};

const EscalationFormWrapper = () => {
    const { t } = useTranslation();
    const params = useParams();
    const navigate = useNavigate();
    const escalationId = params?.escalationId;
    const isEditMode = useMemo(() => !!escalationId && escalationId !== 'new', [escalationId]);

    const {
        data: fetchedEscalationData,
        isLoading,
        error,
    } = useQuery({
        ...escalationDetailQuery(escalationId!),
        enabled: isEditMode,
        retry: 1,
        refetchOnWindowFocus: false,
    });

    const handleBackToEscalations = useCallback(() => {
        navigate('/escalation');
    }, [navigate]);

    const transformedDataForForm = useMemo(() => {
        return isEditMode && fetchedEscalationData
            ? transformEscalationData(fetchedEscalationData)
            : undefined;
    }, [isEditMode, fetchedEscalationData]);

    const formKey = useMemo(
        () => (isEditMode ? escalationId : 'new-escalation'),
        [isEditMode, escalationId]
    );

    if (isEditMode && isLoading) {
        return <FormSkeleton />;
    }

    if (error) {
        return (
            <StateArea>
                <Stack alignItems="center">
                    <ErrorScreen message={t('escalation.loadFailed', 'Failed to load escalation data. Please try again or contact support.')} />
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={handleBackToEscalations}
                        sx={{ mt: 3 }}
                    >
                        {t('escalation.backToEscalations', 'Back to Escalations')}
                    </Button>
                </Stack>
            </StateArea>
        );
    }

    return (
        <EscalationForm key={formKey} initialData={transformedDataForForm} isEditMode={isEditMode} />
    );
};

export default EscalationFormWrapper;
